import { WORKFLOW_CONFIG } from 'constants/workflow2';
import CompanyService from 'services/company2';
import BaseService from 'services/base';
import User from 'services/user2';
import Modal from 'services/modal';
import Http from 'services/http2';
import Util from 'lib/util';
const debug = Util.debug;
const isArray = Array.isArray;

/**
 * 审批流程服务
 */
const WorkflowService = {
  isPurchase: true, //是否采购方
  billXkNo: undefined, //单号

  /** 工作流状态流转 begin */

  /**
   * 初始化配置
   *
   * @param workflow {Object} 该对象必须包含 wfmCode。初始化后，会扩展 arrType, data。
   */
  initConfig(workflow) {
    let config = WORKFLOW_CONFIG[workflow.wfmCode];
    if (config) {
      workflow.arrType = config.list
        .filter(item => item.wfTypeCode)
        .map(item => item.wfTypeCode);
      workflow.billXkNoField = config.billXkNoField;
      //初始化complate属性，避免模板报错
      let data = {};
      workflow.arrType.forEach(code => {
        data[code] = { complete: true, canEdit: true };
      });
      workflow.data = data;
    }
    return config;
  },

  /**
   * 初始化详情页审批流对象
   *
   * @param companyCode {string}
   * @param workflow {Object} 该对象必须包含 wfmCode, billXkNoField, 用于初始化。初始化后，会扩展 arrType, data, isActive属性。
   * @param billXkNo {string} 单号，如果不传，后台不会检查权限
   */
  async init(companyCode, workflow, billXkNo, isPurchase: boolean = true) {
    if (Util.isEmpty(companyCode)) {
      return;
    }
    //缓存参数，用于会签重新获取权限时使用
    this.isPurchase = isPurchase;
    this.billXkNo = billXkNo;

    let config = this.initConfig(workflow);
    debug('init-1', [config, workflow, billXkNo]);
    //列表页不传billXkNo, 只取配置而不判断权限。
    if (!config || workflow.arrType.length === 0 || !billXkNo) {
      return;
    }

    let postbody = {
      companyCode,
      billXkNo,
      wfTypeCodes: workflow.arrType,
      sourceType: isPurchase ? 1 : 2,
      apiPath: '/workflow/getInfosAndAuth'
    };
    await Http.post('/api/cloudCenter', postbody).then(response => {
      if (response.success) {
        let data = response.data || [];
        debug('init-2', data);
        if (data.length > 0) {
          data.forEach(item => {
            debug('init-3', item);

            //当前审批流设置审批关，或启用了OA审批。
            if ((item.useFlag === 1 && item.wfNodeList) || item.useFlag === 2) {
              if (item.wfNodeList) {
                item.wfNodeListOld = item.wfNodeList.map(node => node);
                //条件不符合时需要跳过审批关需要过滤掉
                item.wfNodeList = item.wfNodeList.filter(
                  node => node.skipFlag !== 1
                );
                let length = item.wfNodeList.length;
                if (length === 0) {
                  workflow.data[item.wfTypeCode] = {
                    complete: true,
                    canEdit: true
                  };
                  return;
                }
                item.wfNodeList.forEach((node, i) => {
                  //默认流程状态为初始状态;并记录初始|结束状态编码
                  if (i === 0) {
                    item.wfnXkNo = node.wfnXkNo;
                    item.nodeName = node.nodeName;
                    item.startNode = node.wfnXkNo;
                    item.haveAuthFlag = node.haveAuthFlag;
                  } else if (node.authType === 5) {
                    // 上一关指定，复制员工范围到上一关
                    item.wfNodeList[i - 1].nextEmployeeList =
                      node.preAssignXkNoList;
                  }
                  //结束关编码
                  if (i === length - 1) {
                    item.endNode = node.wfnXkNo;
                  }
                });
              }

              //审批流程重置（撤回）权限码
              if (config && config.list) {
                config.list.forEach(configItem => {
                  if (configItem.wfTypeCode === item.wfTypeCode) {
                    item.resetAuthCode = configItem.resetAuthCode;
                  }
                });
              }

              workflow.data[item.wfTypeCode] = item;
              //开启 && 配置了审批关 && 有权限；此时才会有有效数据。
              workflow.isActive = true;
            } else {
              workflow.data[item.wfTypeCode] = {
                complete: true,
                canEdit: true
              };
            }
          });
        } else {
          //未开启审批流时，按钮需要显示
          workflow.arrType.forEach(code => {
            workflow.data[code] = { complete: true, canEdit: true };
          });
        }
        debug('init-4', workflow);
      } else {
        debug(response);
      }
    });
  },

  /**
   * 初始化列表页审批流对象
   * 仅查询审批流程是否开启，是否设置审批关。
   *
   * @param companyCode {string} 企业编码
   * @param workflow    {Object} 该对象必须包含 wfmCode。初始化后，会扩展 arrType, data, isActive属性。
   */
  async initList(companyCode, workflow) {
    this.initConfig(workflow);
    let postbody = {
      companyCode,
      wfmCode: workflow.wfmCode,
      apiPath: '/workflow/getMainSimpleDetail'
    };
    await Http.post('/api/cloudCenter', postbody).then(response => {
      if (response.success) {
        let data = response.data || [];
        if (data.useFlag === 0) {
          //未启用
          return;
        }
        let infosList = data.infosList;
        if (isArray(infosList)) {
          infosList.forEach(item => {
            //当前审批流有可能未设置审批关。
            if (item.wfNodeList) {
              workflow.data[item.wfTypeCode] = item;
              //开启 && 配置了审批关 && 有权限；此时才会有有效数据。
              workflow.isActive = true;
            }
          });
        }
      }
    });
  },

  /**
   * 获取审批流状态
   *
   * 1. 编辑按钮是否显示: workflow?.data?.wfTypeCode?.canEdit（详情页）
   *                      item?.workflow?.hideBtn === false（列表页删除等按钮）
   * 2. 受控按钮是否显示：workflow.data?.wfTypeCode?.complete（详情页）
   *                    item?.workflow?.isPending === false（列表页）
   * 3. 会签时需要显示审批人列表
   * 4. 审批中是否允许编辑: workflow?.lineEditFlag === 1 (详情页)
   *
   * @param companyCode {string}
   * @param list {array} 列表项次数组或详情页[this.detail]
   * @param workflow {object} 初始化过后的审批流对象，扩展 arrBtn，arrStatus。
   * @param isDetail {boolean} 是否详情页
   */
  async getStatus(companyCode, list, workflow, isDetail = false) {
    let billXkNos: any[] = [];
    list.forEach(item => {
      if (!item.billXkNo) {
        item.billXkNo = item[workflow.billXkNoField];
      }
      if (item.billXkNo) {
        billXkNos.push(item.billXkNo);
      }
    });

    if (Util.isEmpty(billXkNos)) {
      return;
    }

    let arrName = ['退回', '部分同意', '同意', '待']; //审批状态翻译

    let postbody = {
      companyCode,
      billXkNos,
      apiPath: '/workflowBills/getList'
    };
    let data;
    await Http.post('/api/cloudCenter', postbody).then(response => {
      if (response.success) {
        data = response.data || [];
      } else {
        debug(response);
      }
    });

    debug('getStatus-1 [list data]', [list, data]);

    if (!isArray(list) || !isArray(data)) {
      return;
    }

    for (let line of list) {
      //如果是详情页，直接引用workflow；列表页需要克隆后每个项次单独处理。
      line.workflow = isDetail ? workflow : Util.clone(workflow);
      let billStatus = line.status;
      line.workflow.arrBtn = []; //先置空流程按钮
      line.workflow.arrStatus = [];
      line.workflow.lineEditFlag = 0; // 审批中编辑标志

      //for (let wfTypeCode of line.workflow.arrType) {
      if (Array.isArray(line.workflow.arrType)) {
        for (let k = 0; k < line.workflow.arrType.length; k += 1) {
          let wfTypeCode = line.workflow.arrType[k];

          //审批环节
          let item = isDetail
            ? line.workflow.data[wfTypeCode]
            : line.workflow.data[wfTypeCode];

          //如果当前是平台审批，且审批关没有设置
          if (
            item.isActive === false ||
            (item.useFlag === 1 && Util.isEmpty(item.wfNodeList))
          ) {
            //编辑按钮是否显示需要依赖此标识
            item.isActive = false;
            //按钮需要显示
            line.workflow.data[wfTypeCode].complete = true;
            line.workflow.data[wfTypeCode].canEdit = true;
            continue;
          } else {
            //item.isActive = true;
          }

          //从配置信息中复制审批人姓名（当岗位只有一人时，需要显示这个人姓名）
          data.forEach(row => {
            item?.wfNodeList?.forEach(node => {
              if (row.wfnXkNo === node.wfnXkNo) {
                row?.authList?.forEach(targetAuth => {
                  node?.authList?.forEach(sourceAuth => {
                    if (targetAuth.optXkNo === sourceAuth.optXkNo) {
                      targetAuth.checkEmpName = sourceAuth.checkEmpName;
                    }
                  });
                });
              }
            });
          });

          //后端返回的preStatus是字符串，需要转成数组。'[0,11]'
          if (typeof item.preStatus === 'string') {
            item.preStatus = Util.jsonDecode(item.preStatus);
          }

          debug('getStatus-2 [preStatus, billStatus]', [
            item?.preStatus,
            billStatus
          ]);

          //单据状态是审批流的前置状态；
          if (item.preStatus?.includes(billStatus)) {
            let currNode; //当前关

            if (item.wfNodeList) {
              currNode = item.wfNodeList[0]; //默认第一关
            }

            let applyCheckDesc; //送审说明
            //开启送审时，获取送审状态
            if (item.neededApplyCheckFlag === 1) {
              let {
                applyCheckFlag,
                checkDesc,
                checkDate,
                checkUserId,
                checkUserName
              } = await this.getApplyStatus(
                companyCode,
                line.billXkNo,
                item.wfTypeCode
              );
              applyCheckDesc = checkDesc;
              // 记录送审信息
              item.applyStatus = {
                applyCheckFlag,
                checkDesc,
                checkDate,
                checkUserId,
                checkUserName
              };
              debug('getStatus-3 applyCheckFlag', applyCheckFlag);

              //已送审状态赋值，用于锁定单据编辑状态
              item.applyCheckFlag = applyCheckFlag;
              if (applyCheckFlag !== 1) {
                //未送审时，当前关设置为待送审
                currNode = {
                  nodeName: '待送审',
                  applyCheckFlag,
                  isApply: true, //待送审
                  haveAuthFlag: currNode?.haveAuthFlag,
                  resetAuthCode: currNode?.resetAuthCode
                };
                item.nodeName = '待送审';
              } else {
                //已送审时显示第一关
                item.nodeName = currNode?.nodeName;
              }
            } else {
              item.nodeName = currNode?.nodeName;
            }

            //上一关审批状态
            let oStatus;
            data.forEach(row => {
              if (
                row.billXkNo === line.billXkNo &&
                row.wfTypeCode === wfTypeCode
              ) {
                oStatus = {
                  wfTypeCode: row.wfTypeCode,
                  wfnXkNo: row.wfnXkNo,
                  status: row.status,
                  wfName: row.wfTypeName,
                  endFlowFlag: row.endFlowFlag,
                  authList: row.authList,
                  arrStatusName: [row.wfnName + ' ' + arrName[row.status]],
                  checkDesc: row.checkDesc,
                  useFlag: item.useFlag
                };
                if (item.useFlag === 2) {
                  //OA审批
                  oStatus.arrStatusName = [row.wfnName];
                }
              }
            });

            debug('getStatus-3 [item, currNode, oStatus]', [
              item,
              currNode,
              oStatus
            ]);

            if (oStatus) {
              if (oStatus.endFlowFlag === 1) {
                //最后一关且已同意
                item.complete = true;
              } else {
                item.complete = false;
                if (isArray(item.wfNodeList)) {
                  item.wfNodeList.forEach((node, i) => {
                    if (oStatus.wfnXkNo === node.wfnXkNo) {
                      if (oStatus.status === 1) {
                        //部分同意（会签），停留在当前关
                        currNode = item.wfNodeList[i];
                      } else if (oStatus.status === 2) {
                        //全部同意（会签），显示下一关
                        currNode = item.wfNodeList[i + 1] || item.wfNodeList[i]; //最后一关同意时容错
                      } else if (oStatus.status === 0 && i !== 0) {
                        currNode = item.wfNodeList[i - 1];
                      }
                    }
                  });
                }
                if (item.useFlag === 1 && currNode) {
                  //平台审批显示当前关名称和审批人
                  oStatus.arrStatusName.push(currNode.nodeName);
                  if ([0, 2].includes(oStatus.status)) {
                    //前一关状态：0未审批，1.部分审批，2.已审批。当前关未审批时审批人从配置读取。
                    oStatus.authList = currNode.authList;
                  }
                  if (currNode.multiCheck === 1) {
                    //会签
                    oStatus.multiCheck = 1;
                  }
                }
              }
              //审批标识
              oStatus.approved = true;
            } else {
              //未审批过
              if (item.useFlag === 1) {
                //平台审批
                oStatus = {
                  wfName: item.wfTypeName,
                  authList: item.wfNodeList[0]?.authList,
                  arrStatusName: isDetail
                    ? [null, item.nodeName]
                    : [item.nodeName]
                };
                //待送审时不显示审批人
                if (item.applyCheckFlag !== 1 && currNode.isApply) {
                  delete oStatus.authList;
                }
              } else if (item.useFlag === 2) {
                //OA审批
                oStatus = {
                  wfName: item.wfTypeName,
                  arrStatusName: [null, '待OA审批']
                };
                if (currNode?.isApply) {
                  oStatus.arrStatusName = [null, '待送审'];
                }
              }
              //审批标识。OA审批已送审时需要隐藏
              oStatus.approved = currNode?.isApply ? false : true;
            }

            if (oStatus) {
              oStatus.applyCheckDesc = applyCheckDesc;
              // 从当前关获取详情显示标识。
              oStatus.viewDetailPermissionFlag =
                currNode.viewDetailPermissionFlag;
              line.workflow.arrStatus.push(oStatus);

              //列表页隐藏操作按钮，以免审批中的单据被改变
              if (!line.workflow.hideBtn) {
                line.workflow.hideBtn = oStatus.approved;
              }

              //未完成，当前关是会签，且不是部分同意时, 需要从配置项读取审批人列表
              if (
                !item.complete &&
                currNode?.multiCheck === 1 &&
                oStatus.status !== 1
              ) {
                oStatus.multiCheck = 1;
                oStatus.authList = currNode.authList;
              }
            } else {
              if (!line.workflow.hideBtn) {
                line.workflow.hideBtn = false;
              }
            }

            //判断是否审批中, 此时需要隐藏其他业务操作按钮。
            line.workflow.isPending = false;
            //0.审批开启 1.审批流程未结束
            if ([1, 2].includes(item.useFlag) && !item.complete) {
              if (
                item.neededApplyCheckFlag === 1 &&
                item.applyCheckFlag === 1
              ) {
                //0.送审开启 1.送审中
                line.workflow.isPending = true;
              } else if (
                item.neededApplyCheckFlag !== 1 &&
                item.startNode !== currNode?.wfnXkNo
              ) {
                //0.送审未开启 1.审批流不是第一关
                line.workflow.isPending = true;
              }
              //列表页审批中包含待送审
              if (!isDetail && item.neededApplyCheckFlag === 1) {
                line.workflow.isPending = true;
              }
            }

            //列表页审批状态
            if (line.workflow.isPending) {
              line.workflow.status =
                item.neededApplyCheckFlag === 1 && item.applyCheckFlag !== 1
                  ? 0
                  : 1;
              //列表页当前审批关
              let nodeName = item.useFlag === 1 ? currNode.nodeName : 'OA审批';
              line.workflow.currNode =
                item.neededApplyCheckFlag !== 1 || item.applyCheckFlag === 1
                  ? nodeName
                  : null;
            } else if (item.complete) {
              line.workflow.status = 2;
            }
            line.workflow.wfTypeCode = wfTypeCode;

            //判断是否可以编辑，模板上可以根据此字段锁定编辑状态
            item.canEdit = true; //默认可以编辑

            //流程开启，审批完成或审批中时不得编辑。
            if (
              [1, 2].includes(item.useFlag) &&
              (item.complete || line.workflow.isPending)
            ) {
              item.canEdit = false;
            }

            debug('getStatus-4 审批按钮', [
              item.useFlag === 1,
              currNode?.haveAuthFlag === 1,
              !item.complete
            ]);

            //设置工作流按钮 0.平台审批 1.有权限；2.审批流程未结束;
            if (
              item.useFlag === 1 &&
              currNode?.haveAuthFlag === 1 &&
              !item.complete &&
              isArray(item.wfNodeList)
            ) {
              //未来会签升级为岗位中一人审批就算通过时，此处可以由前端来判断，本人或本岗位已签则不再显示按钮
              //todo
              item.wfNodeList.forEach(node => {
                if (node.wfnXkNo === currNode.wfnXkNo) {
                  line.workflow.arrBtn.push({
                    ...node,
                    checkStatus: 1,
                    btnName: '同意',
                    wfTypeCode
                  });
                  line.workflow.arrBtn.push({
                    ...node,
                    checkStatus: 0,
                    btnName: '退回',
                    wfTypeCode
                  });

                  // 审批中编辑标志
                  line.workflow.lineEditFlag = node.lineEditFlag;
                  line.workflow.lineEditFields = Util.jsonDecode(
                    node.lineEditFields
                  );
                }
              });
            }

            debug('getStatus-4 撤回', [
              item.useFlag === 1,
              item.resetAuthCode,
              User.checkAuth(item.resetAuthCode),
              line.workflow.isPending || item.complete
            ]);

            //设置撤回按钮 0.平台审批 1.有权限 2.审批中或已完成
            if (
              item.useFlag === 1 &&
              item.resetAuthCode &&
              User.checkAuth(item.resetAuthCode) &&
              (line.workflow.isPending || item.complete)
            ) {
              line.workflow.arrBtn.push({
                isReset: true,
                checkStatus: 0,
                btnName: '撤回',
                wfTypeCode
              });
            }

            debug('getStatus-4 送审', [
              item.useFlag !== 0,
              currNode,
              !item.complete,
              item.applyCheckFlag !== 1,
              currNode?.isApply,
              item.resetAuthCode,
              User.checkAuth(item.resetAuthCode)
            ]);

            //送审按钮显示逻辑：1.审批开启，2.当前关，3.流程未完成，4.未送审，5.当前关是待送审，6.权限
            if (
              item.useFlag !== 0 &&
              currNode &&
              !item.complete &&
              item.applyCheckFlag !== 1 &&
              currNode.isApply &&
              item.resetAuthCode &&
              User.checkAuth(item.resetAuthCode)
            ) {
              // 第一关是上一关指定时，复制指定人列表
              if (isArray(item.wfNodeList)) {
                let firstNode = item.wfNodeList[0];
                if (firstNode.authType === 5) {
                  currNode.nextEmployeeList = firstNode.preAssignXkNoList;
                }
              }

              line.workflow.arrBtn.push({
                ...currNode,
                btnName: '送审', //'送审'
                wfTypeCode
              });
            }
          }
        }
      }
    }
    debug('getStatus-5 workflow', workflow);
  },

  /**
   * 生成审批记录列表
   *
   * @description 合并显示当前审批环节审批关列表，已审批记录，送审记录
   *
   * @param detail
   * @param workflow
   */
  async createRecordList(detail, workflow) {
    let recordList: any[] = [];
    let postbody = {
      companyCode: detail.companyCode,
      billXkNo: detail.billXkNo,
      wfTypeCode: workflow.wfTypeCode,
      apiPath: '/workflow/check/record/getWfnRecordList'
    };
    let arr: any[] = [];
    await Http.post('/api/cloudCenter', postbody).then(response => {
      if (response.success) {
        arr = response.data || [];
      } else {
        debug(response);
      }
    });

    // 当前审批环节
    let item = workflow.data[workflow.wfTypeCode];

    // 是否开启送审
    if (item?.neededApplyCheckFlag) {
      recordList.push({
        nodeName: '送审',
        applyCheckFlag: item.applyStatus?.applyCheckFlag,
        checkDesc: item.applyStatus?.checkDesc,
        checkUserName: item.applyStatus?.checkUserName, // 送审人
        checkDate: item.applyStatus?.checkDate // 送审时间
      });
    }

    item?.wfNodeListOld?.forEach(node => {
      let record: any = {
        nodeName: node.nodeName,
        wfnXkNo: node.wfnXkNo,
        multiCheck: node.multiCheck,
        authList: node.authList
      };

      arr.forEach(row => {
        if (row.wfnXkNo === node.wfnXkNo) {
          record.status = row.status; // 审批状态
          record.billWfnFlag = row.billWfnFlag; // 当前审批关标识
          record.skipFlag = row.skipFlag; // 跳关标识
          record.recordList = row.recordList; // 审批记录
        }
      });

      recordList.push(record);
    });

    workflow.recordList = recordList;
  },

  /**
   * 检查下一关是否需要设置审批人
   *
   * @param item 当前审批关对象
   * @param workflow 审批配置
   * @returns rs {boolean}
   */
  checkSelectNext(item, workflow) {
    let { toWfnXkCode, wfTypeCode } = item;
    if (item.isApply && item.applyCheckFlag !== 1) {
      // 送审时，检查第一关是否设置为"上一关指定"
      let wfNodeList = workflow.data[wfTypeCode].wfNodeList || [];
      return wfNodeList[0]?.authType === 5;
    } else {
      if (!toWfnXkCode) {
        return false;
      }
      let wfNodeList = workflow.data[wfTypeCode].wfNodeList || [];
      return wfNodeList.some(
        node => node.wfnXkNo === toWfnXkCode && node.authType === 5
      );
    }
  },

  /**
   * 审批（按钮点击）
   *
   * @param companyCode {string}
   * @param billXkNo {string}
   * @param detail {object} 单据详情
   * @param workflow {object} 审批配置
   * @param item {object} 当前审批关对象
   */
  async transfer(
    companyCode,
    billXkNo,
    detail,
    workflow,
    item,
    dispatch,
    callback: Function = () => {}
  ) {
    let ok = async (reason?) => {
      if (item.checkStatus === 0 && Util.isEmpty(reason)) {
        Modal.showError('退回原因不能为空', dispatch);
        return;
      } else if (item.checkStatus === 1 && Util.isEmpty(reason)) {
        Modal.showError('审批意见不能为空', dispatch);
        return;
      }
      let config = {
        companyCode,
        billXkNo,
        wfnXkNo: item.wfnXkNo,
        checkStatus: item.checkStatus,
        optXkNoList: item.optXkNoList,
        checkDesc: reason,
        wfTypeCode: item.wfTypeCode,
        apiPath: '/workflowBills/checkFlow',
        successMsg: '操作成功'
      };
      let rs;
      let cb = () => {
        if (
          item.checkStatus === 1 &&
          (item.multiCheck === 1 || item.isSelectNext)
        ) {
          //会签成功后 || 指定下一关审批人 ，需要重新调用权限接口
          this.init(companyCode, workflow, this.billXkNo, this.isPurchase).then(
            () => {
              this.getStatus(companyCode, [detail], workflow, true);
            }
          );
        } else {
          this.getStatus(companyCode, [detail], workflow, true);
        }
        dispatch({ type: 'hideReject' });
        dispatch({ type: 'hideWfConfirm' });
        dispatch({ type: 'onChangeTest' });
      };
      await BaseService.save(config, cb, dispatch).then(response => {
        rs = response;
        if (rs.success) {
          dispatch({ type: 'refresh' });
          if (
            rs.data?.autoPublishFlag === 1 &&
            typeof callback === 'function'
          ) {
            callback(rs.data);
          }
        }
      });
      return rs;
    };

    //指定下一关审批人
    if (item.isSelectNext) {
      ok(item.reasonStr);
      return;
    }
    let title = '退回原因';
    let reasonStr = '';
    if (item.checkStatus === 1) {
      title = '审批意见';
      reasonStr = '同意';
    }
    Modal.showReject('操作提示', ok, title, 'showReject', reasonStr, dispatch);
  },

  /**
   * 重设审批流（按钮点击）
   *
   * @param companyCode {string}
   * @param billXkNo {string}
   * @param detail {object} 单据详情
   * @param workflow {object} 审批配置
   * @param item {object} 当前审批关对象
   */
  async reset(companyCode, billXkNo, detail, workflow, item, dispatch) {
    let ok = async () => {
      let config = {
        companyCode,
        billXkNo,
        wfTypeCode: item.wfTypeCode,
        apiPath: '/workflowBills/resetWorkFlow',
        successMsg: '操作成功'
      };
      let rs;
      let cb = () => {
        //重置complete
        workflow.data[item.wfTypeCode].complete = false;
        this.getStatus(companyCode, [detail], workflow, true);
      };
      await BaseService.save(config, cb, dispatch).then(response => {
        rs = response;
      });
      return rs;
    };
    Modal.showConfirm('确认撤回审批？', ok, dispatch);
  },

  /**
   * 获取送审状态
   *
   * @param companyCode {string}
   * @param billXkNo {string}
   * @param item {object} 当前审批关对象
   * @returns applyCheckFlag 送审状态
   */
  async getApplyStatus(companyCode, billXkNo, wfTypeCode) {
    let postbody = {
      companyCode,
      billXkNo,
      wfTypeCode,
      apiPath: '/workflowBills/applyCheck/list'
    };
    let applyCheckFlag;
    await Http.post('/api/cloudCenter', postbody).then(response => {
      if (response.success) {
        applyCheckFlag = response.data;
      }
    });
    return applyCheckFlag;
  },

  /**
   * 设置送审状态
   *
   * @param companyCode {string}
   * @param billXkNo {string}
   * @param item {object} 当前审批关对象
   * @param applyCheckFlag {number} 送审状态
   */
  async setApplyStatus(
    companyCode,
    billXkNo,
    btnItem,
    applyCheckFlag,
    checkDesc,
    cb,
    dispatch
  ) {
    let ok = async () => {
      let config = {
        companyCode,
        billXkNo,
        wfTypeCode: btnItem.wfTypeCode,
        optXkNoList: btnItem.optXkNoList,
        applyCheckFlag,
        checkDesc,
        apiPath: '/workflowBills/applyCheck/saveOrUpdate',
        successMsg: '操作成功'
      };
      await CompanyService.save(config).then(response => {
        Modal.showTip('操作成功', dispatch);
        if (typeof cb === 'function') {
          cb(response.success);
        }
      });
    };

    ok();
    //await Modal.showConfirm('您是否确认此操作？', ok, dispatch);
  }

  /** 工作流状态流转 end */
};
export default WorkflowService;
